import type { Message } from '../api/types'

/**
 * A message with nothing to draw (1.20.1).
 *
 * No text, no media, no card of any kind, and not deleted: `Bubble` renders
 * an empty row for it -- every branch there hangs on text, an attachment or
 * a card. Such rows exist: the migration text of the highlights update said
 * `ON DELETE CASCADE` for `media.highlight_id` where the schema says
 * `SET NULL`, and a database that followed it lost the shared photo of a
 * post when the point made from it was deleted. The post stayed, empty,
 * and got a day divider over nothing -- from use, with a screenshot.
 *
 * The server keeps the row (a hole in the history would tear the paging);
 * the page treats it like a deleted one. One place for the question, so
 * the list, the unread edge and the divider agree.
 */
export function istLeer(m: Message): boolean {
  return (
    !m.deleted
    && m.body === ''
    && m.media.length === 0
    && (m.highlights?.items.length ?? 0) === 0
    && m.poll === null
    && (m.weather ?? null) === null
    && (m.day_card ?? null) === null
    // Music (1.93.0, #119): a post of a song whose caption was emptied is a
    // song, not nothing. Until 1.92 it was missing here, and the chat dropped
    // such a post as empty -- the server allows it, `music_cards` counts.
    && (m.music ?? null) === null
    // A post that is only a day separator (#113) is a chapter line, not nothing.
    && (m.day_separator ?? null) === null
  )
}

/**
 * A day separator post (#130, 1.115.0): a post of its own that carries the
 * line and nothing else -- the server refuses text or photos with it. It
 * draws the line and no bubble, in the chat (`Bubble`) and in the book
 * (`Beitrag`). Deleted, it is deleted like any post: its line goes with it
 * (until 1.114 the line moved on to the next post, `moveOn()`).
 */
export function nurTrenner(m: Message): boolean {
  return (m.day_separator ?? null) !== null && !m.deleted
}

/**
 * Whether a row stands in the chat at all (1.93.0, #119): not empty and not
 * deleted. One place for the page, the unread edge and the search for it,
 * so that the three count the same rows.
 */
export function imVerlauf(m: Message): boolean {
  return !istLeer(m) && !m.deleted
}
